/**
 * Settings Hook and Provider
 *
 * Manages user preferences with localStorage persistence:
 * - topN: Number of items in charts (5, 10, 15, 20)
 * - language: UI language ('ar' or 'en')
 * - datePreset: Active date filter preset
 *
 * Also manages server-side global settings:
 * - refreshInterval: Auto-refresh interval for MVs (global setting, stored on server)
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// API URL for refresh settings (server-side global setting). Uses the orchestrator
// URL baked at build time (REACT_APP_MAIN_API_URL); the hostname fallback keeps it
// working when that var is unset.
const API_URL = process.env.REACT_APP_MAIN_API_URL || `http://${window.location.hostname}:8206`;

const SettingsContext = createContext();

// Default settings
const DEFAULT_SETTINGS = {
  topN: 10,
  language: 'ar',
  datePreset: 'all',
  startDate: null,
  endDate: null
};

// localStorage key
const STORAGE_KEY = 'sera_dashboard_settings';

/**
 * Load settings from localStorage
 */
function loadSettings() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new settings
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (e) {
    console.warn('Failed to load settings from localStorage:', e);
  }
  return { ...DEFAULT_SETTINGS };
}

/**
 * Save settings to localStorage
 */
function saveSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn('Failed to save settings to localStorage:', e);
  }
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(loadSettings);

  // Server-side global settings for auto-refresh
  const [refreshInterval, setRefreshIntervalState] = useState(0);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [nextRefresh, setNextRefresh] = useState(null);

  // Persist settings to localStorage whenever they change
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  // Update a single setting
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Update multiple settings at once
  const updateSettings = useCallback((updates) => {
    setSettings(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Reset to defaults (both localStorage and server-side settings)
  const resetSettings = useCallback(async () => {
    // Reset localStorage settings
    setSettings({ ...DEFAULT_SETTINGS });

    // Reset server-side refresh interval to default (60 minutes)
    try {
      const response = await fetch(
        `${API_URL}/api/v1/dashboard/refresh-settings?interval_minutes=60`,
        { method: 'POST' }
      );
      if (response.ok) {
        const data = await response.json();
        setRefreshIntervalState(data.interval_minutes || 0);
        setNextRefresh(data.next_refresh || null);
      }
    } catch (e) {
      console.warn('Failed to reset server refresh settings:', e);
    }
  }, []);

  // Set date preset (convenience method)
  const setDatePreset = useCallback((preset, startDate = null, endDate = null) => {
    setSettings(prev => ({
      ...prev,
      datePreset: preset,
      startDate: preset === 'custom' ? startDate : null,
      endDate: preset === 'custom' ? endDate : null
    }));
  }, []);

  // Set top N (convenience method)
  const setTopN = useCallback((n) => {
    const validValues = [5, 10, 15, 20];
    const value = validValues.includes(n) ? n : 10;
    updateSetting('topN', value);
  }, [updateSetting]);

  // Set language (convenience method)
  const setLanguage = useCallback((lang) => {
    if (lang === 'ar' || lang === 'en') {
      updateSetting('language', lang);

      // Update document direction for RTL/LTR
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = lang;
    }
  }, [updateSetting]);

  // Toggle language (convenience method)
  const toggleLanguage = useCallback(() => {
    setLanguage(settings.language === 'ar' ? 'en' : 'ar');
  }, [settings.language, setLanguage]);

  // ============================================================================
  // SERVER-SIDE REFRESH SETTINGS
  // ============================================================================

  // Fetch refresh settings from server
  const fetchRefreshSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/dashboard/refresh-settings`);
      if (response.ok) {
        const data = await response.json();
        setRefreshIntervalState(data.interval_minutes || 0);
        setNextRefresh(data.next_refresh || null);
      }
    } catch (error) {
      console.warn('Failed to fetch refresh settings:', error);
    }
  }, []);

  // Update refresh interval on server
  const setRefreshInterval = useCallback(async (minutes) => {
    setRefreshLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/v1/dashboard/refresh-settings?interval_minutes=${minutes}`,
        { method: 'POST' }
      );
      if (response.ok) {
        const data = await response.json();
        setRefreshIntervalState(data.interval_minutes || 0);
        setNextRefresh(data.next_refresh || null);
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Failed to update' };
      }
    } catch (error) {
      console.error('Failed to update refresh settings:', error);
      return { success: false, error: error.message };
    } finally {
      setRefreshLoading(false);
    }
  }, []);

  // Fetch refresh settings on mount
  useEffect(() => {
    fetchRefreshSettings();
  }, [fetchRefreshSettings]);

  // Apply document direction on mount and language change
  useEffect(() => {
    document.documentElement.dir = settings.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = settings.language;
  }, [settings.language]);

  const value = {
    // Current settings (localStorage)
    ...settings,

    // Update methods
    updateSetting,
    updateSettings,
    resetSettings,

    // Convenience methods
    setDatePreset,
    setTopN,
    setLanguage,
    toggleLanguage,

    // Server-side refresh settings
    refreshInterval,
    refreshLoading,
    nextRefresh,
    setRefreshInterval,
    fetchRefreshSettings,

    // Computed values
    isRTL: settings.language === 'ar',
    hasCustomDateRange: settings.datePreset === 'custom' && settings.startDate && settings.endDate
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export default useSettings;
